import React, {useEffect, useState, useCallback, useRef} from 'react';
import {Form, Typography, Space, Button, Tooltip} from 'antd';
import UserSelect from '../../../core/AntdSelect/UserSelect';
import UserCardAntd from '../../../core/UserCard/UserCardAntd';
import {
  FileTextOutlined,
  EditOutlined,
  EyeOutlined,
  TrophyOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import {ExecutionSetupType, UserDTO, UserType, ExternalUserType} from 'hemwb-api';
import {trackPermissionsTabEvent} from '../../../../tracking/tracking';
import {ModelEditPermissionsFormData} from '../../model/common/ModelEdit/ModelEditPermissons/ModelEditPermissionsForm';
import {useDispatch, useSelector} from 'react-redux';
import {authLoggedInUserSelector} from '../../../../store/auth/selectors';
import {fullName} from '../../../../store/user/utils';
import {getModelSteward, modifyModelMetadata, isModelWeb, isModelTheorem} from '../../../../store/model';
import {canEditTHModelAccess} from '../../../../permissions/usePermissions';
import {hardCodedOptions} from '../../model/common/ModelEdit/ModelEditMetadata/constants';
import {messageAdd, MessageTypes} from '../../../../store/message';
import {useSideBarModal} from '../../../layout/common/useSideBarModal';
import SideBarModal from '../../../layout/common/SideBarModal';
import UserSettingsForm from '../../model/common/ModelEdit/ModelEditPermissons/UserSettingsForm';
import {useExecutionSetupList} from '../../../../store/executionSetup';
import {useLastValidValue} from '../../../../hooks/useLastValidValue';
import WarningTextWithTitle from '../../../core/WarningText/WarningTextWithTitle';
import styles from './StepPermissionsRedesigned.module.scss';
const {Title, Paragraph, Text} = Typography;

interface StepPermissionsRedesignedProps {
  permissions: ModelEditPermissionsFormData;
  onSubmit: (permissions: ModelEditPermissionsFormData) => void;
  onChange: (permissions: ModelEditPermissionsFormData) => void;
  onBack?: () => void;
  buttons?: (params: {onBack?: () => void; nextHtmlType?: 'button' | 'submit' | 'reset'}) => React.ReactNode;
  webModel?: boolean;
}

const StepPermissionsRedesigned: React.FC<StepPermissionsRedesignedProps> = ({
  permissions,
  onSubmit,
  onChange,
  onBack,
  buttons,
  webModel: webModelProp,
}) => {
  // Calculate webModel from the actual model metadata, not just the prop
  const webModel = isModelWeb(permissions.model) || webModelProp || false;

  const classificationOptions = hardCodedOptions['information_classification'];
  const theoremModel = isModelTheorem(permissions.model);
  const sensitiveModel =
    classificationOptions && permissions.model?.metadata?.information_classification === classificationOptions[3];
  const errorText = theoremModel ? 'Theorem model' : 'Sensitive model';

  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const loggedInUser = useSelector(authLoggedInUserSelector);
  const [hedsLeadWarning, setHedsLeadWarning] = useState<string | null>(null);
  // Cache of all known UserDTO objects for ID→UserDTO resolution in handleFormChange.
  // Populated from permissions on init and from onSelect callbacks (which fire before onValuesChange).
  const userPool = useRef<Map<string, UserDTO>>(new Map());
  const {buttonRef, isOpen, openModal, closeModal, user} = useSideBarModal(permissions.model?.uuid);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const calibrationList = useExecutionSetupList(permissions.model?.uuid, ExecutionSetupType.CALIBRATION);
  const scenarioList = useExecutionSetupList(permissions.model?.uuid, ExecutionSetupType.SCENARIO);
  const storedCalibrationList = useLastValidValue(calibrationList);
  const storedScenarioList = useLastValidValue(scenarioList);

  useEffect(() => {
    trackPermissionsTabEvent();
  }, []);

  const handleCancelSubModelIds = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleUpdateSubModelIds = useCallback((id: number, selected: boolean) => {
    setSelectedIds((previous) => {
      if (selected) {
        return previous.includes(id) ? previous : [...previous, id];
      }
      return previous.filter((selectedId) => selectedId !== id);
    });
  }, []);

  const disableCheckbox = useCallback(() => {
    const userIds: string[] = [];
    permissions.owners?.forEach((owner) => {
      if (owner.id) {
        userIds.push(owner.id);
      }
    });

    if (permissions.vestLead?.id) {
      userIds.push(permissions.vestLead.id);
    }

    const modelSteward = getModelSteward(permissions.model);
    if (modelSteward?.id) {
      userIds.push(modelSteward.id);
    }

    if (user?.id) {
      return userIds.includes(user.id);
    }
  }, [permissions, user]);

  const showUserSettings = webModel && !!permissions.model?.uuid;

  const renderCardActions = useCallback(
    (currentUser: UserDTO, onDelete: () => void, includeUserSettings = true, canDelete = true) => {
      return (
        <Space
          size={8}
          style={{
            marginLeft: '12px',
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          {showUserSettings && includeUserSettings && (
            <Button
              size="small"
              style={{height: '3rem', whiteSpace: 'nowrap'}}
              onClick={(event) => openModal(event, currentUser)}
            >
              User Settings
            </Button>
          )}
          {canDelete && <Button type="text" size="small" icon={<DeleteOutlined />} onClick={onDelete} />}
        </Space>
      );
    },
    [openModal, showUserSettings],
  );

  const renderPermissionUserCard = useCallback(
    (
      currentUser: UserDTO,
      onDelete: () => void,
      options?: {
        includeUserSettings?: boolean;
        canDelete?: boolean;
        marginBottom?: string;
      },
    ) => {
      return (
        <UserCardAntd
          user={currentUser}
          showStatusTags
          rightContent={renderCardActions(
            currentUser,
            onDelete,
            options?.includeUserSettings ?? true,
            options?.canDelete ?? true,
          )}
          style={{marginBottom: options?.marginBottom ?? '8px'}}
        />
      );
    },
    [renderCardActions],
  );

  // Initialize form values and user pool from permissions
  useEffect(() => {
    const modelSteward = getModelSteward(permissions.model);

    form.setFieldsValue({
      vestLead: permissions.vestLead?.id ? [permissions.vestLead.id] : [],
      modelSteward: modelSteward?.id ? [modelSteward.id] : [],
      owners: permissions.owners?.map((u) => u.id) || [],
      viewers: permissions.viewers?.map((u) => u.id) || [],
      calibrationModelers: permissions.calibrationModelers?.map((u) => u.id) || [],
      scenarioModelers: permissions.scenarioModelers?.map((u) => u.id) || [],
      displayViewers: permissions.displayViewers?.map((u) => u.id) || [],
    });

    // Populate user pool so handleFormChange can resolve IDs → UserDTO
    const known: (UserDTO | undefined | null)[] = [
      permissions.vestLead,
      modelSteward,
      ...(permissions.owners || []),
      ...(permissions.viewers || []),
      ...(permissions.calibrationModelers || []),
      ...(permissions.scenarioModelers || []),
      ...(permissions.displayViewers || []),
    ];
    known.filter(Boolean).forEach((u) => {
      if (u!.id) userPool.current.set(u!.id, u!);
    });
  }, [permissions, form]);

  // Called by UserSelect to pre-cache users for handleFormChange
  const cacheUser = useCallback((u: UserDTO) => {
    if (u?.id) userPool.current.set(u.id, u);
  }, []);

  const getLastSelectedId = useCallback((value: any): string | undefined => {
    if (Array.isArray(value)) {
      return value[value.length - 1];
    }
    return value;
  }, []);

  const normalizeSingleSelection = useCallback(
    (value: any): string[] => {
      const last = getLastSelectedId(value);
      return last ? [last] : [];
    },
    [getLastSelectedId],
  );

  // Helper function to remove users from other fields when they're added to a new field
  const removeUsersFromOtherFields = useCallback((newUserIds: string[], targetField: string, allFormValues: any) => {
    const fieldsToCheck = ['owners', 'viewers', 'calibrationModelers', 'scenarioModelers', 'displayViewers'];
    const updatedValues = {...allFormValues};

    fieldsToCheck.forEach((field) => {
      if (field !== targetField && updatedValues[field]) {
        updatedValues[field] = updatedValues[field].filter((id: string) => !newUserIds.includes(id));
      }
    });

    return updatedValues;
  }, []);

  // Validate vestLead selection - no external users (pool is pre-populated by cacheUser via onUserSelect)
  const validateVestLead = useCallback(
    (userId: string) => {
      const u = userPool.current.get(userId);
      if (u?.userType === UserType.EXTERNALUSER) {
        dispatch(messageAdd('External user cannot be added as the Value and Implementation Lead.', MessageTypes.ERROR));
        return false;
      }
      return true;
    },
    [dispatch],
  );

  // Validate modelSteward selection - no external users
  const validateModelSteward = useCallback(
    (userId: string) => {
      const u = userPool.current.get(userId);
      if (u?.userType === UserType.EXTERNALUSER) {
        dispatch(messageAdd('External user cannot be added as the HEDS Lead.', MessageTypes.ERROR));
        return false;
      }
      return true;
    },
    [dispatch],
  );

  // Validate owners selection - no HTA external users, no external users on theorem/sensitive models
  const validateOwners = useCallback(
    (userIds: string[]) => {
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          if (u?.externalUserType === ExternalUserType.HTA) {
            dispatch(messageAdd('HTA User cannot be assigned as an Editor.', MessageTypes.ERROR));
            return false;
          }
          if (theoremModel || sensitiveModel) {
            dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
            return false;
          }
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  // Validate viewers selection - no HTA external users, no external users on theorem/sensitive models
  const validateViewers = useCallback(
    (userIds: string[]) => {
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          if (u?.externalUserType === ExternalUserType.HTA) {
            dispatch(messageAdd('HTA User cannot be assigned as a Model Viewer.', MessageTypes.ERROR));
            return false;
          }
          if (theoremModel || sensitiveModel) {
            dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
            return false;
          }
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  // Validate calibration/scenario/display modelers - no external users on theorem/sensitive models
  const validateExternalUserForSensitiveModel = useCallback(
    (userIds: string[]): boolean => {
      if (!(theoremModel || sensitiveModel)) return true;
      for (const userId of userIds) {
        const u = userPool.current.get(userId);
        if (u?.userType === UserType.EXTERNALUSER) {
          dispatch(messageAdd(`External user access cannot be added as this is a ${errorText}.`, MessageTypes.ERROR));
          return false;
        }
      }
      return true;
    },
    [dispatch, theoremModel, sensitiveModel, errorText],
  );

  const handleFormChange = (changedValues: any, allValues: any) => {
    // Validate vestLead if changed
    if (changedValues.vestLead !== undefined) {
      const vestLeadId = getLastSelectedId(changedValues.vestLead);
      if (vestLeadId && !validateVestLead(vestLeadId)) {
        form.setFieldValue('vestLead', permissions.vestLead?.id ? [permissions.vestLead.id] : []);
        return;
      }
    }

    // Validate modelSteward if changed (detect both selections and deletions/clears)
    if ('modelSteward' in changedValues || 'owners' in changedValues) {
      const modelStewardId = getLastSelectedId(changedValues.modelSteward);
      if (modelStewardId && !validateModelSteward(modelStewardId)) {
        const currentSteward = getModelSteward(permissions.model);
        form.setFieldValue('modelSteward', currentSteward?.id ? [currentSteward.id] : []);
        return;
      }

      // Check if user has access to the model (either as HEDS Lead or Editor)
      // This applies to both adding and removing users
      if (loggedInUser?.id) {
        const isHedsLead = getLastSelectedId(allValues.modelSteward) === loggedInUser.id;
        const isEditor = allValues.owners?.includes(loggedInUser.id);

        // Show warning ONLY if user has NO access in either role
        if (!isHedsLead && !isEditor) {
          setHedsLeadWarning(
            "You're removing yourself from the HEDS Lead role. You'll lose edit access to this entry. Add yourself as an Editor if you still need to make changes",
          );
        } else {
          // User has access in at least one role, clear warning
          setHedsLeadWarning(null);
        }
      }
    }

    // Validate owners if changed
    if (changedValues.owners !== undefined) {
      if (changedValues.owners && !validateOwners(changedValues.owners)) {
        form.setFieldValue('owners', permissions.owners?.map((u) => u.id) || []);
        return;
      }
    }

    // Validate viewers if changed
    if (changedValues.viewers !== undefined) {
      if (changedValues.viewers && !validateViewers(changedValues.viewers)) {
        form.setFieldValue('viewers', permissions.viewers?.map((u) => u.id) || []);
        return;
      }
    }

    // Validate calibrationModelers if changed
    if (changedValues.calibrationModelers !== undefined) {
      if (
        changedValues.calibrationModelers &&
        !validateExternalUserForSensitiveModel(changedValues.calibrationModelers)
      ) {
        form.setFieldValue('calibrationModelers', permissions.calibrationModelers?.map((u) => u.id) || []);
        return;
      }
    }

    // Validate scenarioModelers if changed
    if (changedValues.scenarioModelers !== undefined) {
      if (changedValues.scenarioModelers && !validateExternalUserForSensitiveModel(changedValues.scenarioModelers)) {
        form.setFieldValue('scenarioModelers', permissions.scenarioModelers?.map((u) => u.id) || []);
        return;
      }
    }

    // Validate displayViewers if changed
    if (changedValues.displayViewers !== undefined) {
      if (changedValues.displayViewers && !validateExternalUserForSensitiveModel(changedValues.displayViewers)) {
        form.setFieldValue('displayViewers', permissions.displayViewers?.map((u) => u.id) || []);
        return;
      }
    }

    let processedValues = {
      ...allValues,
      vestLead: normalizeSingleSelection(allValues.vestLead),
      modelSteward: normalizeSingleSelection(allValues.modelSteward),
    };

    // Handle cross-field user removal for WebModel fields
    if (webModel) {
      if (changedValues.owners) {
        processedValues = removeUsersFromOtherFields(changedValues.owners, 'owners', processedValues);
        // Also remove from WebModel fields
        ['calibrationModelers', 'scenarioModelers', 'displayViewers'].forEach((field) => {
          if (processedValues[field]) {
            processedValues[field] = processedValues[field].filter((id: string) => !changedValues.owners.includes(id));
          }
        });
      }
      if (changedValues.viewers) {
        processedValues = removeUsersFromOtherFields(changedValues.viewers, 'viewers', processedValues);
        // Also remove from WebModel fields
        ['calibrationModelers', 'scenarioModelers', 'displayViewers'].forEach((field) => {
          if (processedValues[field]) {
            processedValues[field] = processedValues[field].filter((id: string) => !changedValues.viewers.includes(id));
          }
        });
      }
      if (changedValues.calibrationModelers) {
        // Remove from scenarioModelers
        if (processedValues.scenarioModelers) {
          processedValues.scenarioModelers = processedValues.scenarioModelers.filter(
            (id: string) => !changedValues.calibrationModelers.includes(id),
          );
        }
      }
      if (changedValues.scenarioModelers) {
        // Remove from calibrationModelers
        if (processedValues.calibrationModelers) {
          processedValues.calibrationModelers = processedValues.calibrationModelers.filter(
            (id: string) => !changedValues.scenarioModelers.includes(id),
          );
        }
      }
      if (changedValues.displayViewers) {
        // Remove from other WebModel fields and viewers
        processedValues = removeUsersFromOtherFields(changedValues.displayViewers, 'displayViewers', processedValues);
      }

      // Remove modelSteward from all WebModel fields if changed
      const selectedModelStewardId = getLastSelectedId(processedValues.modelSteward);
      if (selectedModelStewardId) {
        ['calibrationModelers', 'scenarioModelers', 'viewers', 'displayViewers'].forEach((field) => {
          if (processedValues[field]) {
            processedValues[field] = processedValues[field].filter((id: string) => id !== selectedModelStewardId);
          }
        });
      }
    }

    // Update form with processed values
    if (JSON.stringify(processedValues) !== JSON.stringify(allValues)) {
      form.setFieldsValue(processedValues);
    }

    // Convert IDs back to UserDTO objects via the pool (populated from permissions + onSelect callbacks)
    const pool = userPool.current;
    const selectedVestLeadId = getLastSelectedId(processedValues.vestLead);
    const selectedModelStewardId = getLastSelectedId(processedValues.modelSteward);
    const modelSteward = selectedModelStewardId ? pool.get(selectedModelStewardId) : undefined;

    const updatedPermissions: ModelEditPermissionsFormData = {
      ...permissions,
      vestLead: selectedVestLeadId ? pool.get(selectedVestLeadId) : undefined,
      owners: processedValues.owners
        ? (processedValues.owners.map((id: any) => pool.get(id)).filter(Boolean) as UserDTO[])
        : [],
      viewers: processedValues.viewers
        ? (processedValues.viewers.map((id: any) => pool.get(id)).filter(Boolean) as UserDTO[])
        : [],
      calibrationModelers: processedValues.calibrationModelers
        ? (processedValues.calibrationModelers.map((id: any) => pool.get(id)).filter(Boolean) as UserDTO[])
        : [],
      scenarioModelers: processedValues.scenarioModelers
        ? (processedValues.scenarioModelers.map((id: any) => pool.get(id)).filter(Boolean) as UserDTO[])
        : [],
      displayViewers: processedValues.displayViewers
        ? (processedValues.displayViewers.map((id: any) => pool.get(id)).filter(Boolean) as UserDTO[])
        : [],
      model: modelSteward
        ? modifyModelMetadata(permissions.model, {
            isid_of_model_steward: modelSteward.id || '',
            email_for_model_steward: modelSteward.email || '',
            name_of_model_steward: fullName(modelSteward) || '',
          })
        : permissions.model,
    };
    onChange(updatedPermissions);
  };

  const handleFinish = (_values: any) => {
    onSubmit(permissions);
  };

  return (
    <div
      style={{background: '#ffffff', minHeight: '100vh', padding: '40px 80px'}}
      className={styles.permissionsContainer}
    >
      <div style={{maxWidth: '1400px', margin: '0 auto'}}>
        <div className={styles.mainGrid}>
          {/* Left side - Understanding Roles */}
          <div
            style={{
              background: '#fff',
              padding: '0',
            }}
          >
            <Title level={4} style={{marginBottom: '16px', color: '#006A63', fontSize: '20px', fontWeight: 500}}>
              Understanding Roles and Permissions
            </Title>
            <Paragraph type="secondary" style={{fontSize: '13px', marginBottom: '24px', color: '#595959'}}>
              Set up the right access levels for your model entry
            </Paragraph>

            <div
              style={{
                background: '#fff',
                padding: '0',
              }}
            >
              <Space direction="vertical" size="large" style={{width: '100%'}}>
                <div>
                  <Space align="start">
                    <TrophyOutlined style={{color: '#006A63', fontSize: '18px', marginTop: '2px'}} />
                    <div>
                      <Text style={{fontSize: '14px', display: 'block', color: '#595959'}}>BARDS HEDS Lead</Text>
                      <Text type="secondary" style={{fontSize: '12px', color: '#666'}}>
                        Responsible for storing, sharing and updating model files and documentation. Manages roles and
                        permissions for the model entry. Can delete and retire the model entry.
                      </Text>
                    </div>
                  </Space>
                </div>

                <div>
                  <Space align="start">
                    <FileTextOutlined style={{color: '#006A63', fontSize: '18px', marginTop: '2px'}} />
                    <div>
                      <Text style={{fontSize: '14px', display: 'block', color: '#595959'}}>
                        Value & Implementation Lead
                      </Text>
                      <Text type="secondary" style={{fontSize: '12px', color: '#666'}}>
                        Has read-only access like viewers; is also CC'ed on model access requests.
                      </Text>
                    </div>
                  </Space>
                </div>

                <div>
                  <Space align="start">
                    <EditOutlined style={{color: '#006A63', fontSize: '18px', marginTop: '2px'}} />
                    <div>
                      <Text style={{fontSize: '14px', display: 'block', color: '#595959'}}>Editor</Text>
                      <Text type="secondary" style={{fontSize: '12px', color: '#666'}}>
                        Editor is the primary role responsible for a model in Model Repository.
                      </Text>
                    </div>
                  </Space>
                </div>

                <div>
                  <Space align="start">
                    <EyeOutlined style={{color: '#006A63', fontSize: '18px', marginTop: '2px'}} />
                    <div>
                      <Text style={{fontSize: '14px', display: 'block', color: '#595959'}}>Viewers</Text>
                      <Text type="secondary" style={{fontSize: '12px', color: '#666'}}>
                        Read-only access to view and download model files and documentation.
                      </Text>
                    </div>
                  </Space>
                </div>
              </Space>

              <div style={{borderTop: '1px solid #d9d9d9', marginTop: '24px', paddingTop: '16px'}}>
                <Text type="secondary" style={{fontSize: '12px', color: '#666'}}>
                  You are automatically assigned as the HEDS Lead with full editor permissions.
                </Text>
              </div>
            </div>
          </div>

          {/* Right side - Setup Permissions Form */}
          <div>
            <Title level={4} style={{marginBottom: '16px', color: '#262626', fontSize: '20px', fontWeight: 500}}>
              Setup Permissions
            </Title>
            <Paragraph style={{fontSize: '13px', color: '#595959', marginBottom: '24px'}}>
              Set up the right access levels for your model entry
            </Paragraph>

            <Form form={form} layout="vertical" onFinish={handleFinish} onValuesChange={handleFormChange}>
              <div
                style={{
                  border: '2px dashed #d9d9d9',
                  borderRadius: '8px',
                  padding: '40px',
                  background: '#fafafa',
                }}
              >
                {/* Two-column layout for Model Roles and Access Permissions */}
                <div className={`${styles.permissionsGrid} ${webModel ? styles.withWebModel : ''}`}>
                  {/* Permissions to Edit Column */}
                  <div style={{minWidth: 0}}>
                    <Title
                      level={5}
                      style={{marginBottom: '20px', fontSize: '16px', fontWeight: 500, color: '#006A63'}}
                    >
                      Permissions to Edit
                    </Title>

                    <Form.Item
                      label={
                        <span>
                          BARDS HEDS Lead <span style={{color: '#ff4d4f'}}>*</span>
                        </span>
                      }
                      name="modelSteward"
                      required={false}
                      rules={[{required: true, message: 'Please select HEDS Lead'}]}
                      colon={false}
                      style={{marginBottom: getModelSteward(permissions.model) ? '8px' : '16px'}}
                    >
                      <UserSelect
                        mode="multiple"
                        placeholder="Find user"
                        onUserSelect={cacheUser}
                        selectedUsers={getModelSteward(permissions.model) ?? undefined}
                        getOptionDisabled={(user) => getModelSteward(permissions.model)?.id === user.id}
                        disabled={theoremModel}
                        hideSelectedValue
                        maxTagCount={0}
                        style={{width: '100%'}}
                      />
                    </Form.Item>

                    {/* Display HEDS Lead removal warning */}
                    {hedsLeadWarning && (
                      <div style={{marginBottom: '16px'}}>
                        <WarningTextWithTitle title="Warning" text={hedsLeadWarning} />
                      </div>
                    )}

                    {/* Display selected HEDS Lead user card */}
                    {(() => {
                      const modelSteward = getModelSteward(permissions.model);
                      return modelSteward
                        ? renderPermissionUserCard(
                            modelSteward,
                            () => {
                              form.setFieldValue('modelSteward', []);
                              onChange({
                                ...permissions,
                                model: modifyModelMetadata(permissions.model, {
                                  isid_of_model_steward: '',
                                  email_for_model_steward: '',
                                  name_of_model_steward: '',
                                }),
                                // Also explicitly clear the modelSteward in permissions to reflect current state
                              });
                              // Manually trigger the warning check since this bypasses onValuesChange
                              if (loggedInUser?.id && modelSteward?.id === loggedInUser.id) {
                                const currentOwners = form.getFieldValue('owners') || [];
                                const isUserInOwners = currentOwners.includes(loggedInUser.id);
                                // Only warn if user is removing from BOTH HEDS Lead and Editor roles
                                if (!isUserInOwners) {
                                  setHedsLeadWarning(
                                    "You're removing yourself from the HEDS Lead role. You'll lose edit access to this entry. Add yourself as an Editor if you still need to make changes",
                                  );
                                } else {
                                  setHedsLeadWarning(null);
                                }
                              } else {
                                setHedsLeadWarning(null);
                              }
                            },
                            {includeUserSettings: true, marginBottom: '16px', canDelete: !theoremModel},
                          )
                        : null;
                    })()}

                    <Form.Item
                      label={
                        <span>
                          Editor <span style={{color: '#ff4d4f'}}>*</span>
                        </span>
                      }
                      name="owners"
                      required={false}
                      rules={[{required: true, message: 'Please select at least one Editor'}]}
                      colon={false}
                      style={{marginBottom: permissions.owners?.length ? '8px' : '0'}}
                    >
                      <UserSelect
                        mode="multiple"
                        placeholder="Select person"
                        onUserSelect={cacheUser}
                        selectedUsers={permissions.owners}
                        getOptionDisabled={(user) => !!permissions.owners?.some((u) => u.id === user.id)}
                        disabled={theoremModel}
                        hideSelectedValue
                        maxTagCount={0}
                        style={{width: '100%'}}
                      />
                    </Form.Item>

                    {/* Display selected Editor user cards */}
                    {permissions.owners?.map((owner) => (
                      <React.Fragment key={owner.id}>
                        {renderPermissionUserCard(
                          owner,
                          () => {
                            const currentOwners = form.getFieldValue('owners') || [];
                            const newOwners = currentOwners.filter((id: any) => id !== owner.id);
                            form.setFieldValue('owners', newOwners);
                            onChange({
                              ...permissions,
                              owners: (permissions.owners || []).filter((u) => newOwners.includes(u.id)),
                            });
                            // Manually trigger the warning check since this bypasses onValuesChange
                            // Only warn if the logged-in user is being removed from Editor AND they're not HEDS Lead
                            const modelSteward = getModelSteward(permissions.model);
                            const isUserHedsLead = modelSteward?.id === loggedInUser?.id;
                            // Only warn if user is removing from BOTH HEDS Lead and Editor roles
                            if (!isUserHedsLead) {
                              setHedsLeadWarning(
                                "You're removing yourself from the HEDS Lead role. You'll lose edit access to this entry. Add yourself as an Editor if you still need to make changes",
                              );
                            } else {
                              setHedsLeadWarning(null);
                            }
                          },
                          {canDelete: !theoremModel},
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Permissions to View and Download Column */}
                  <div style={{minWidth: 0}}>
                    <Title
                      level={5}
                      style={{marginBottom: '20px', fontSize: '16px', fontWeight: 500, color: '#006A63'}}
                    >
                      Permissions to View and Download
                    </Title>

                    <Form.Item
                      label={
                        <span>
                          Value & Implementation Lead <span style={{color: '#ff4d4f'}}>*</span>
                        </span>
                      }
                      name="vestLead"
                      required={false}
                      rules={[{required: true, message: 'Please select Value & Implementation Lead'}]}
                      colon={false}
                      style={{marginBottom: permissions.vestLead ? '8px' : '16px'}}
                    >
                      <UserSelect
                        mode="multiple"
                        placeholder="Find user"
                        onUserSelect={cacheUser}
                        selectedUsers={permissions.vestLead ?? undefined}
                        getOptionDisabled={(user) => permissions.vestLead?.id === user.id}
                        disabled={theoremModel}
                        hideSelectedValue
                        maxTagCount={0}
                        style={{width: '100%'}}
                      />
                    </Form.Item>

                    {/* Display selected Value & Implementation Lead user card */}
                    {permissions.vestLead &&
                      renderPermissionUserCard(
                        permissions.vestLead,
                        () => {
                          form.setFieldValue('vestLead', []);
                          onChange({...permissions, vestLead: undefined});
                        },
                        {marginBottom: '16px', canDelete: !theoremModel},
                      )}

                    <Form.Item
                      label="Viewers"
                      name="viewers"
                      style={{marginBottom: permissions.viewers?.length ? '8px' : '0'}}
                    >
                      <UserSelect
                        mode="multiple"
                        placeholder="Find user"
                        onUserSelect={cacheUser}
                        selectedUsers={permissions.viewers}
                        getOptionDisabled={(user) =>
                          getModelSteward(permissions.model)?.id === user.id ||
                          !!permissions.owners?.some((u) => u.id === user.id) ||
                          !!permissions.viewers?.some((u) => u.id === user.id)
                        }
                        disabled={theoremModel && !canEditTHModelAccess(permissions.model)}
                        hideSelectedValue
                        maxTagCount={0}
                        style={{width: '100%'}}
                      />
                    </Form.Item>

                    {/* Display selected Viewers user cards */}
                    {permissions.viewers?.map((user) => (
                      <React.Fragment key={user.id}>
                        {renderPermissionUserCard(
                          user,
                          () => {
                            const currentViewers = form.getFieldValue('viewers') || [];
                            const newViewers = currentViewers.filter((id: any) => id !== user.id);
                            form.setFieldValue('viewers', newViewers);
                            onChange({
                              ...permissions,
                              viewers: (permissions.viewers || []).filter((u) => newViewers.includes(u.id)),
                            });
                          },
                          {canDelete: !(theoremModel && !canEditTHModelAccess(permissions.model))},
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* WebModel 2.0 specific roles section - separate from main permissions */}
                {webModel && (
                  <div style={{marginTop: '32px'}}>
                    <Title
                      level={5}
                      style={{marginBottom: '20px', fontSize: '16px', fontWeight: 500, color: '#006A63'}}
                    >
                      WebModel 2.0 specific roles
                    </Title>

                    {/* Two-column layout for WebModel permissions */}
                    <div className={styles.permissionsGrid}>
                      {/* Left Column - Calibration Management Permission */}
                      <div style={{minWidth: 0}}>
                        <Form.Item
                          label={
                            <span>
                              Calibration Management Permission{' '}
                              <Tooltip title="Calibration Modeler: a Model User who can provide, execute, and accept calibration inputs for use in scenario analysis. This role also has the same permissions as Scenario Modeler and Model Display Viewer. The role does not have access to download the model entry files.">
                                <QuestionCircleOutlined style={{color: '#8c8c8c'}} />
                              </Tooltip>
                            </span>
                          }
                          name="calibrationModelers"
                          style={{marginBottom: '8px'}}
                        >
                          <UserSelect
                            mode="multiple"
                            placeholder="Select Calibration Modeler"
                            onUserSelect={cacheUser}
                            selectedUsers={permissions.calibrationModelers}
                            getOptionDisabled={(user) =>
                              getModelSteward(permissions.model)?.id === user.id ||
                              !!permissions.owners?.some((u) => u.id === user.id) ||
                              !!permissions.viewers?.some((u) => u.id === user.id) ||
                              !!permissions.calibrationModelers?.some((u) => u.id === user.id)
                            }
                            hideSelectedValue
                            maxTagCount={0}
                            style={{width: '100%'}}
                            disabled={theoremModel}
                          />
                        </Form.Item>

                        {permissions.calibrationModelers?.map((user) => (
                          <React.Fragment key={user.id}>
                            {renderPermissionUserCard(
                              user,
                              () => {
                                const currentModelers = form.getFieldValue('calibrationModelers') || [];
                                const newModelers = currentModelers.filter((id: any) => id !== user.id);
                                form.setFieldValue('calibrationModelers', newModelers);
                                onChange({
                                  ...permissions,
                                  calibrationModelers: (permissions.calibrationModelers || []).filter((u) =>
                                    newModelers.includes(u.id),
                                  ),
                                });
                              },
                              {canDelete: !theoremModel},
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* Right Column - Scenario Management Permission and Display User Permissions */}
                      <div style={{minWidth: 0}}>
                        <Form.Item
                          label={
                            <span>
                              Scenario Management Permission{' '}
                              <Tooltip title="Scenario Modeler: a Model User who can provide scenario inputs and execute scenario analysis. This role also has the same permissions as Model Display Viewer. This role does not have access to download the model entry files.">
                                <QuestionCircleOutlined style={{color: '#8c8c8c'}} />
                              </Tooltip>
                            </span>
                          }
                          name="scenarioModelers"
                          style={{marginBottom: '8px'}}
                        >
                          <UserSelect
                            mode="multiple"
                            placeholder="Select Scenario Modelers"
                            onUserSelect={cacheUser}
                            selectedUsers={permissions.scenarioModelers}
                            getOptionDisabled={(user) =>
                              getModelSteward(permissions.model)?.id === user.id ||
                              !!permissions.owners?.some((u) => u.id === user.id) ||
                              !!permissions.viewers?.some((u) => u.id === user.id) ||
                              !!permissions.calibrationModelers?.some((u) => u.id === user.id) ||
                              !!permissions.scenarioModelers?.some((u) => u.id === user.id)
                            }
                            hideSelectedValue
                            maxTagCount={0}
                            style={{width: '100%'}}
                            disabled={theoremModel}
                          />
                        </Form.Item>

                        {permissions.scenarioModelers?.map((user) => (
                          <React.Fragment key={user.id}>
                            {renderPermissionUserCard(
                              user,
                              () => {
                                const currentModelers = form.getFieldValue('scenarioModelers') || [];
                                const newModelers = currentModelers.filter((id: any) => id !== user.id);
                                form.setFieldValue('scenarioModelers', newModelers);
                                onChange({
                                  ...permissions,
                                  scenarioModelers: (permissions.scenarioModelers || []).filter((u) =>
                                    newModelers.includes(u.id),
                                  ),
                                });
                              },
                              {canDelete: !theoremModel},
                            )}
                          </React.Fragment>
                        ))}

                        <Form.Item
                          label={
                            <span>
                              Display User Permissions{' '}
                              <Tooltip title="Model Display Viewer: a Model User who can view results of calibration and scenario analysis. This role does not have access to download the model entry files.">
                                <QuestionCircleOutlined style={{color: '#8c8c8c'}} />
                              </Tooltip>
                            </span>
                          }
                          name="displayViewers"
                          style={{marginBottom: '8px', marginTop: '24px'}}
                        >
                          <UserSelect
                            mode="multiple"
                            placeholder="Select Model Display View"
                            onUserSelect={cacheUser}
                            selectedUsers={permissions.displayViewers}
                            getOptionDisabled={(user) =>
                              getModelSteward(permissions.model)?.id === user.id ||
                              !!permissions.owners?.some((u) => u.id === user.id) ||
                              !!permissions.displayViewers?.some((u) => u.id === user.id)
                            }
                            hideSelectedValue
                            maxTagCount={0}
                            style={{width: '100%'}}
                            disabled={theoremModel}
                          />
                        </Form.Item>

                        {permissions.displayViewers?.map((user) => (
                          <React.Fragment key={user.id}>
                            {renderPermissionUserCard(
                              user,
                              () => {
                                const currentViewers = form.getFieldValue('displayViewers') || [];
                                const newViewers = currentViewers.filter((id: any) => id !== user.id);
                                form.setFieldValue('displayViewers', newViewers);
                                onChange({
                                  ...permissions,
                                  displayViewers: (permissions.displayViewers || []).filter((u) =>
                                    newViewers.includes(u.id),
                                  ),
                                });
                              },
                              {canDelete: !theoremModel},
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Buttons */}
              {buttons && buttons({onBack, nextHtmlType: 'submit'})}
            </Form>

            <SideBarModal
              className="whiteTheme"
              isOpen={isOpen}
              buttonRef={buttonRef}
              closeModal={closeModal}
              height={613}
            >
              <div style={{width: '60rem'}}>
                {buttonRef && user && (
                  <UserSettingsForm
                    user={user}
                    modelUuid={permissions.model?.uuid}
                    disableCheckbox={disableCheckbox}
                    isOpen={isOpen}
                    closeModal={closeModal}
                    buttonRef={buttonRef}
                    handleUpdateSubModelIds={handleUpdateSubModelIds}
                    handleCancelSubModelIds={handleCancelSubModelIds}
                    storedCalibrationList={storedCalibrationList}
                    storedScenarioList={storedScenarioList}
                    selectedIds={selectedIds}
                  />
                )}
              </div>
            </SideBarModal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepPermissionsRedesigned;
